import { useEffect, useState } from 'react';
import cc from 'classcat';
import { SkeletonAvatarSize } from 'utils/constants/app/ui';
import { COMPETITION_LOGO_LINK, TEAM_LOGO_LINK } from 'utils/constants/logos';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { rand } from 'utils/swarm/rid-generator';
import { ETeamLogosOptionTypes } from 'interfaces/generic';
import { Skeleton } from 'newcomponents/UI/Skeleton';
import { useMounted } from 'hooks/useMounted';
import './index.less';

type Props = {
  teamId?: number;
  children?: JSX.Element;
  size: 'sm' | 'md' | 'lg';
  setHasImage?: (hasImage: boolean) => void;
  competition?: boolean;
  imageLoadingType?: 'lazy' | 'eager';
  sizeByPixels?: number;
  isHomeOrAway?: number;
  className?: string;
  isNotESport?: boolean;
};

const TEAM_ID_DIVISOR = 2000;
const IMAGES_PER_FOLDER = 500;

export const TeamLogo = ({
  teamId,
  children,
  size,
  setHasImage,
  competition,
  imageLoadingType = 'lazy',
  sizeByPixels,
  isHomeOrAway,
  className = '',
  isNotESport = false
}: Props): JSX.Element | null => {
  const [imageSrc, setImageSrc] = useState<string>();
  const [loading, setLoading] = useState(!!teamId);
  const [onLoaded, setOnLoaded] = useState(false);
  const imageSizeVar = size === 'sm' ? 's' : size === 'md' ? 'm' : 'b';
  const { mounted } = useMounted();

  useEffect(() => {
    if (!teamId) {
      return;
    }

    if (
      !SpringConfigs.TEAM_LOGOS ||
      SpringConfigs.TEAM_LOGOS_TYPE === ETeamLogosOptionTypes.LETTER
    ) {
      setLoading(false);
      setHasImage?.(false);

      return;
    }

    const img = new Image();

    if (
      SpringConfigs.TEAM_LOGOS_TYPE === ETeamLogosOptionTypes.TSHIRT &&
      isNotESport
    ) {
      const folder = Math.floor(teamId / IMAGES_PER_FOLDER);
      const homeOrAway = isHomeOrAway === 2 ? 2 : 1;
      const urlKit = `https://statistics.bcapps.site/images/sh/${folder}/${homeOrAway}-${teamId}.png??v=${rand()}`;

      img.src = urlKit;

      img.onload = () => {
        if (!mounted) {
          return;
        }

        setImageSrc(urlKit);
        setLoading(false);
        setHasImage?.(true);
      };
    } else {
      img.src = `${
        competition ? COMPETITION_LOGO_LINK : TEAM_LOGO_LINK
      }${imageSizeVar}/${Math.floor(teamId / TEAM_ID_DIVISOR)}/${teamId}.webp`;

      img.onload = () => {
        if (!mounted) {
          return;
        }

        setImageSrc(img.src);
        setLoading(false);
        setHasImage?.(true);
      };
    }

    img.onerror = () => {
      if (!mounted) {
        return;
      }

      setImageSrc('');
      setHasImage?.(false);
      setLoading(false);
    };
  }, [teamId]);

  if (loading && SpringConfigs.TEAM_LOGOS) {
    return (
      <Skeleton.Avatar
        size={
          sizeByPixels ||
          (imageSizeVar === 'b'
            ? SkeletonAvatarSize.SIZE_60
            : SkeletonAvatarSize.SIZE_24)
        }
        active
        className={className}
      />
    );
  }

  if (imageSrc && SpringConfigs.TEAM_LOGOS) {
    return (
      <>
        {!onLoaded && (
          <Skeleton.Avatar
            className={cc(['teamLogoImage--skeleton', className])}
            size={
              sizeByPixels ||
              (imageSizeVar === 'b'
                ? SkeletonAvatarSize.SIZE_60
                : SkeletonAvatarSize.SIZE_24)
            }
            active
          />
        )}
        <div className={cc(['teamLogoImage__content', className])}>
          <img
            className="teamLogoImage"
            src={imageSrc}
            alt={`${teamId}`}
            loading={imageLoadingType}
            onLoad={() => setOnLoaded(true)}
          />
        </div>
      </>
    );
  }

  if (SpringConfigs.TEAM_LOGOS) {
    return children || null;
  }

  return null;
};
